import { getDateByTimeZone } from 'src/helpers/date_helper';
import { convertCost, convertValueAndUnit } from 'src/helpers/utilities';
import { user_data, user_settings } from 'src/stores/account/user';
import { get } from 'svelte/store';
import type { CalendarHeatmapData } from '@Shelly/shelly-design-system';
import type { GetStatsResponseHistoryData } from 'src/types/requests/charts/get_stats_period';
import { getHeatmapCalendarBounds, getHeatmapYearRange } from './heatmap_period_helpers';

export type EnergyHeatmapScope = 'all' | 'room' | 'device';
export type EnergyHeatmapMetric = 'consumption' | 'generation';

export type EnergyHeatmapModel = {
	calendarData: CalendarHeatmapData;
	totalLabel: string;
	minLabel: string;
	maxLabel: string;
	averageLabel: string;
	activeDayCount: number;
	peakDateLabel: string;
	metric: EnergyHeatmapMetric;
	yearLabel: string;
	monthlySummaries: EnergyHeatmapMonthlySummary[];
};

export type EnergyHeatmapMonthlySummary = {
	monthKey: string;
	monthLabel: string;
	minLabel: string;
	averageLabel: string;
	maxLabel: string;
	activeDayCount: number;
};

export type MinimalEnergyHeatmapCell = {
	date: Date;
	label: string;
	value: number;
	intensity: number;
	isCurrentMonth: boolean;
	isFuture: boolean;
};

export type MinimalEnergyHeatmapModel = {
	cells: MinimalEnergyHeatmapCell[];
	monthLabel: string;
	totalLabel: string;
	totalCostLabel: string;
	peakHourLabel: string;
	trendPercent: number | null;
	trendDirection: 'up' | 'down' | 'flat';
};

const nowInUserTimezone = (): Date => getDateByTimeZone(get(user_data)?.timezone);

export const parseEnergyHistoryDate = (datetime: string): Date => {
	const [datePart, timePart = '00:00:00'] = datetime.split(' ');
	const [year, month, day] = datePart.split('-').map(Number);
	const [hours, minutes, seconds] = timePart.split(':').map(Number);
	return new Date(year, month - 1, day, hours || 0, minutes || 0, seconds || 0);
};

const bucketKey = (date: Date): string => `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;

export const generationWhFromHistoryEntry = (entry: GetStatsResponseHistoryData): number => {
	// PV history contains a calculated total generation field. Prefer it even
	// when it is zero: reversed/exported energy describes grid flow and is not
	// interchangeable with the inverter's complete production.
	if (entry.generated_energy != null) return Math.abs(Number(entry.generated_energy) || 0);
	if (entry.reversed != null) return Math.abs(Number(entry.reversed) || 0);
	return Math.abs(Number(entry.export_energy) || 0);
};

export const formatHeatmapWhLabel = (wh: number): string => {
	if (wh <= 0) return '0 Wh';
	const { value, unit } = convertValueAndUnit(wh, 'Wh');
	return `${value} ${unit}`;
};

const getCalendarDisplayScale = (maxWh: number): { divisor: number; unit: string } => {
	if (maxWh > 999_999) return { divisor: 1_000_000, unit: 'MWh' };
	if (maxWh > 999) return { divisor: 1_000, unit: 'kWh' };
	return { divisor: 1, unit: 'Wh' };
};

const scaleCalendarValue = (wh: number, divisor: number): number => Number((wh / divisor).toFixed(2));

const aggregateHistory = (history: GetStatsResponseHistoryData[]) => {
	const buckets = new Map<string, { consumption: number; generation: number; date: Date }>();

	history.forEach((entry) => {
		if (!entry?.datetime) return;
		const date = parseEnergyHistoryDate(entry.datetime);
		const key = bucketKey(date);
		const existing = buckets.get(key) ?? {
			consumption: 0,
			generation: 0,
			date,
		};
		existing.consumption += entry.consumption ?? 0;
		existing.generation += generationWhFromHistoryEntry(entry);
		buckets.set(key, existing);
	});

	return buckets;
};

const metricValue = (
	bucket: { consumption: number; generation: number } | undefined,
	metric: EnergyHeatmapMetric,
): number => (metric === 'generation' ? (bucket?.generation ?? 0) : (bucket?.consumption ?? 0));

export const buildDemoHeatmapHistory = (): GetStatsResponseHistoryData[] => {
	const now = nowInUserTimezone();
	const { from, to } = getHeatmapYearRange(now);
	const entries: GetStatsResponseHistoryData[] = [];
	const cursor = new Date(from);
	cursor.setHours(12, 0, 0, 0);

	let index = 0;
	while (cursor.getTime() <= to.getTime()) {
		const weekdayFactor = 0.85 + (cursor.getDay() % 5) * 0.05;
		const consumption = Math.round((900 + (index % 7) * 180) * weekdayFactor);
		const generation = Math.round(420 + (index % 5) * 120);
		entries.push({
			datetime: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')} 12:00:00`,
			consumption,
			reversed: generation,
			cost: consumption * 0.00025,
		});
		cursor.setDate(cursor.getDate() + 1);
		index += 1;
	}

	return entries;
};

export const buildDemoMinimalComparisonHistory = (
	now: Date = nowInUserTimezone(),
): GetStatsResponseHistoryData[] => {
	const entries: GetStatsResponseHistoryData[] = [];
	const start = new Date(now);
	start.setDate(start.getDate() - 1);
	start.setHours(0, 0, 0, 0);
	const end = new Date(now);
	end.setMinutes(0, 0, 0);
	const cursor = new Date(start);

	while (cursor.getTime() <= end.getTime()) {
		const hour = cursor.getHours();
		const eveningPeak = Math.max(0, 7 - Math.abs(18 - hour));
		const consumption = Math.round(180 + eveningPeak * 95 + (hour % 4) * 25);
		entries.push({
			datetime: `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}-${String(cursor.getDate()).padStart(2, '0')} ${String(hour).padStart(2, '0')}:00:00`,
			consumption,
			reversed: Math.max(0, Math.round(260 - Math.abs(12 - hour) * 45)),
			cost: consumption * 0.00025,
		});
		cursor.setHours(cursor.getHours() + 1);
	}

	return entries;
};

const startOfDay = (date: Date): Date => {
	const result = new Date(date);
	result.setHours(0, 0, 0, 0);
	return result;
};

const mondayIndex = (date: Date): number => (date.getDay() + 6) % 7;

export const buildMinimalEnergyHeatmapModel = (
	monthHistory: GetStatsResponseHistoryData[],
	comparisonHistory: GetStatsResponseHistoryData[],
	metric: EnergyHeatmapMetric = 'consumption',
	now: Date = nowInUserTimezone(),
): MinimalEnergyHeatmapModel => {
	const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
	const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 1);
	const gridStart = new Date(monthStart);
	gridStart.setDate(gridStart.getDate() - mondayIndex(gridStart));
	const monthBuckets = aggregateHistory(monthHistory);
	const currentMonthValues = Array.from(monthBuckets.values())
		.filter((bucket) => bucket.date >= monthStart && bucket.date < monthEnd)
		.map((bucket) => metricValue(bucket, metric));
	const positiveValues = currentMonthValues.filter((value) => value > 0);
	const minValue = positiveValues.length ? Math.min(...positiveValues) : 0;
	const maxValue = positiveValues.length ? Math.max(...positiveValues) : 0;
	const intensityForValue = (value: number): number => {
		if (value <= 0) return 0;
		if (maxValue <= minValue) return 3;
		return Math.min(5, Math.max(1, Math.ceil(((value - minValue) / (maxValue - minValue)) * 5)));
	};
	const cells = Array.from({ length: 42 }, (_, index): MinimalEnergyHeatmapCell => {
		const date = new Date(gridStart);
		date.setDate(gridStart.getDate() + index);
		const bucket = monthBuckets.get(bucketKey(date));
		const value = metricValue(bucket, metric);
		return {
			date,
			label: date.toLocaleDateString(undefined, {
				weekday: 'long',
				month: 'short',
				day: 'numeric',
			}),
			value,
			intensity: intensityForValue(value),
			isCurrentMonth: date >= monthStart && date < monthEnd,
			isFuture: date.getTime() > now.getTime(),
		};
	});

	const today = startOfDay(now);
	const yesterday = new Date(today);
	yesterday.setDate(yesterday.getDate() - 1);
	const hourlyToday = new Map<number, number>();
	let todayTotal = 0;
	let yesterdayTotal = 0;
	comparisonHistory.forEach((entry) => {
		if (!entry?.datetime) return;
		const date = parseEnergyHistoryDate(entry.datetime);
		const value = metricValue(
			{
				consumption: entry.consumption ?? 0,
				generation: generationWhFromHistoryEntry(entry),
			},
			metric,
		);
		const day = startOfDay(date).getTime();
		if (day === today.getTime()) {
			todayTotal += value;
			hourlyToday.set(date.getHours(), (hourlyToday.get(date.getHours()) ?? 0) + value);
		} else if (day === yesterday.getTime()) {
			yesterdayTotal += value;
		}
	});
	const peakHour =
		hourlyToday.size > 1
			? [...hourlyToday.entries()].sort(([, first], [, second]) => second - first)[0]?.[0]
			: undefined;
	const trendPercent =
		yesterdayTotal > 0 ? Math.round(((todayTotal - yesterdayTotal) / yesterdayTotal) * 100) : null;
	const reportedCostEntries = monthHistory.filter((entry) => {
		if (!entry?.datetime || entry.cost == null || entry.missing) return false;
		const date = parseEnergyHistoryDate(entry.datetime);
		return date >= monthStart && date < monthEnd;
	});
	const totalCost = reportedCostEntries.reduce(
		(sum, entry) => sum + Math.max(Number(entry.cost), 0),
		0,
	);
	const currency = get(user_settings)?.currency ?? '';

	return {
		cells,
		monthLabel: monthStart.toLocaleDateString(undefined, {
			month: 'long',
			year: 'numeric',
		}),
		totalLabel: formatHeatmapWhLabel(currentMonthValues.reduce((sum, value) => sum + value, 0)),
		totalCostLabel:
			metric === 'consumption' && reportedCostEntries.length
				? convertCost(totalCost, currency)
				: '—',
		peakHourLabel: peakHour === undefined ? '—' : `${String(peakHour).padStart(2, '0')}:00`,
		trendPercent,
		trendDirection:
			trendPercent === null || trendPercent === 0 ? 'flat' : trendPercent > 0 ? 'up' : 'down',
	};
};

export const buildEnergyHeatmapModel = (
	history: GetStatsResponseHistoryData[],
	metric: EnergyHeatmapMetric = 'consumption',
	now: Date = nowInUserTimezone(),
): EnergyHeatmapModel => {
	const buckets = aggregateHistory(history);
	const { start, end } = getHeatmapCalendarBounds(now);
	const endOfToday = new Date(now);
	endOfToday.setHours(23, 59, 59, 999);

	const data = Array.from(buckets.values())
		.filter(
			(bucket) =>
				bucket.date.getTime() >= start.getTime() && bucket.date.getTime() < end.getTime(),
		)
		.map((bucket) => ({
			date: bucket.date,
			value: metricValue(bucket, metric),
			label: bucket.date.toLocaleDateString(undefined, {
				weekday: 'long',
				month: 'short',
				day: 'numeric',
			}),
		}));

	const pastValues = data
		.filter((point) => point.date.getTime() <= endOfToday.getTime())
		.map((point) => point.value);
	const totalValue = pastValues.reduce((sum, value) => sum + value, 0);
	const positivePoints = data.filter(
		(point) => point.date.getTime() <= endOfToday.getTime() && point.value > 0,
	);
	const positiveValues = positivePoints.map((point) => point.value);
	const minValue = positiveValues.length ? Math.min(...positiveValues) : 0;
	const maxValue = positiveValues.length ? Math.max(...positiveValues) : 0;
	const calendarScale = getCalendarDisplayScale(maxValue);
	const averageValue = positiveValues.length ? totalValue / positiveValues.length : 0;
	const peakPoint = positivePoints.find((point) => point.value === maxValue);
	const monthlyValues = new Map<string, { date: Date; values: number[] }>();
	positivePoints.forEach((point) => {
		const monthKey = `${point.date.getFullYear()}-${String(point.date.getMonth() + 1).padStart(2, '0')}`;
		const summary = monthlyValues.get(monthKey) ?? {
			date: point.date,
			values: [],
		};
		summary.values.push(point.value);
		monthlyValues.set(monthKey, summary);
	});
	const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
	const monthlySummaries = Array.from({ length: 12 }, (_, month) => {
		const date = new Date(start.getFullYear(), month, 1);
		if (date.getTime() > currentMonthStart.getTime()) return null;
		const monthKey = `${date.getFullYear()}-${String(month + 1).padStart(2, '0')}`;
		const values = monthlyValues.get(monthKey)?.values ?? [];
		const monthTotal = values.reduce((sum, value) => sum + value, 0);
		return {
			monthKey,
			monthLabel: date.toLocaleDateString(undefined, {
				month: 'long',
				year: 'numeric',
			}),
			minLabel: formatHeatmapWhLabel(values.length ? Math.min(...values) : 0),
			averageLabel: formatHeatmapWhLabel(values.length ? monthTotal / values.length : 0),
			maxLabel: formatHeatmapWhLabel(values.length ? Math.max(...values) : 0),
			activeDayCount: values.length,
		};
	}).filter(Boolean) as EnergyHeatmapMonthlySummary[];

	return {
		calendarData: {
			start,
			end,
			unit: calendarScale.unit,
			data: data.map((point) => ({
				...point,
				value: scaleCalendarValue(point.value, calendarScale.divisor),
			})),
			scaleDomain: [
				scaleCalendarValue(minValue, calendarScale.divisor),
				scaleCalendarValue(maxValue, calendarScale.divisor),
			],
		},
		totalLabel: formatHeatmapWhLabel(totalValue),
		minLabel: formatHeatmapWhLabel(minValue),
		maxLabel: formatHeatmapWhLabel(maxValue),
		averageLabel: formatHeatmapWhLabel(averageValue),
		activeDayCount: positiveValues.length,
		peakDateLabel: peakPoint?.label ?? '',
		metric,
		yearLabel: String(now.getFullYear()),
		monthlySummaries,
	};
};

export const heatmapMetricColorSchema = (metric: EnergyHeatmapMetric): 'red' | 'green' =>
	metric === 'generation' ? 'green' : 'red';
